import { Project } from './../projects/projects.types';
import { ProjectComparisonDto, ProjectDto } from './../../models/models';
import { BosatlasDto, BundleDetailDto, BundleDto } from '../../models/models';
import {
	getBosatlas,
	getBosatlasSearchSuggestions,
	searchBosatlas
} from '../../services/BosatlasService';
import {
	addToBundle,
	createNewBundle,
	getBundle,
	getBundleList,
	removeBundle,
	searchBundles,
	updateExistingBundle
} from '../../services/BundleService';
import { AppThunkAction, AppThunkDispatch } from '../app.types';
import {
	BosatlasAction,
	BosatlasComparisonAction,
	BosatlasProjectAction,
	BosatlasSuggestionAction,
	Bundle,
	BosatlasBundleAction,
	BosatlasBundleDeleteAction,
	BosatlasBundlesAction,
	IBosatlasState
} from './bosatlas.types';
import {
	getProjectComparison,
	removeProjectComparison,
	updateProjectComparison
} from '../../services/ProjectComparisonService';
import {
	getProject,
	removeProject,
	updateProject
} from '../../services/ProjectService';
import { Comparison } from '../comparisons/comparison.types';
import {
	SelectedComparison,
	SelectedProject
} from '../project-selector/project-selector.types';

//#region Bundle action strings
export const BOSATLAS_BUNDLE_CREATE: string = 'BOSATLAS_BUNDLE_CREATE';
export const BOSATLAS_BUNDLE_CREATE_COMPLETED: string =
	'BOSATLAS_BUNDLE_CREATE_COMPLETED';

export const BOSATLAS_BUNDLE_FETCH: string = 'BOSATLAS_BUNDLE_FETCH';
export const BOSATLAS_BUNDLE_FETCH_LIST: string = 'BOSATLAS_BUNDLE_FETCH_LIST';

export const BOSATLAS_BUNDLE_LOAD: string = 'BOSATLAS_BUNDLE_LOAD';
export const BOSATLAS_BUNDLE_LOAD_LIST: string = 'BOSATLAS_BUNDLE_LOAD_LIST';
export const BOSATLAS_BUNDLE_UPDATE: string = 'BOSATLAS_BUNDLE_UPDATE';
export const BOSATLAS_BUNDLE_DELETE: string = 'BOSATLAS_BUNDLE_DELETE';
export const BOSATLAS_BUNDLE_DELETE_COMPLETE: string =
	'BOSATLAS_BUNDLE_DELETE_COMPLETE';

export const BOSATLAS_BUNDLE_ADD_CONTENT: string =
	'BOSATLAS_BUNDLE_ADD_CONTENT';
export const BOSATLAS_BUNDLE_REMOVE_PROJECT: string =
	'BOSATLAS_BUNDLE_REMOVE_PROJECT';
export const BOSATLAS_BUNDLE_REMOVE_COMPARISON: string =
	'BOSATLAS_BUNDLE_REMOVE_COMPARISON';
export const BOSATLAS_BUNDLE_CLEAR: string = 'BOSATLAS_BUNDLE_CLEAR';
//#endregion

export const BOSATLAS_COMPARISON_DELETE: string = 'BOSATLAS_COMPARISON_DELETE';
export const BOSATLAS_COMPARISON_UPDATE: string = 'BOSATLAS_COMPARISON_UPDATE';
export const BOSATLAS_PROJECT_DELETE: string = 'BOSATLAS_PROJECT_DELETE';
export const BOSATLAS_PROJECT_UPDATE: string = 'BOSATLAS_PROJECT_UPDATE';
export const BOSATLAS_FETCH: string = 'BOSATLAS_FETCH';
export const BOSATLAS_LOAD: string = 'BOSATLAS_LOAD';
export const BOSATLAS_GET_SUGGESTIONS: string = 'BOSATLAS_GET_SUGGESTIONS';
export const BOSATLAS_LOAD_SUGGESTIONS: string = 'BOSATLAS_LOAD_SUGGESTIONS';
export const BOSATLAS_SEARCH: string = 'BOSATLAS_SEARCH';

export function fetchBosatlas(): AppThunkAction<void, BosatlasAction> {
	return async (dispatch: AppThunkDispatch<BosatlasAction>) => {
		dispatch({ type: BOSATLAS_FETCH });
		const bosatlas: BosatlasDto = await getBosatlas();
		dispatch(loadBosatlas(bosatlas));

		trackBosatlas(bosatlas);
	};
}

export function loadBosatlas(bosatlas: BosatlasDto): BosatlasAction {
	return {
		type: BOSATLAS_LOAD,
		payload: bosatlasDtoToBosatlasState(bosatlas)
	};
}

//#region Bundle actions
export function bosatlasClearBundle(): BosatlasBundleAction {
	return { type: BOSATLAS_BUNDLE_CLEAR };
}

export function bosatlasCreateBundle(
	title: string
): AppThunkAction<void, BosatlasBundleAction> {
	return async (dispatch: AppThunkDispatch<BosatlasBundleAction>) => {
		dispatch({ type: BOSATLAS_BUNDLE_CREATE });

		const newBundleId: string = await createNewBundle(title);

		dispatch({ type: BOSATLAS_BUNDLE_CREATE_COMPLETED });
		dispatch(bosatlasFetchBundle(newBundleId));
	};
}

export function bosatlasFetchBundle(
	id: string
): AppThunkAction<void, BosatlasBundleAction> {
	return async (dispatch: AppThunkDispatch<BosatlasBundleAction>) => {
		dispatch({ type: BOSATLAS_BUNDLE_FETCH });

		const bundle: BundleDetailDto = await getBundle(id);

		dispatch(bosatlasLoadBundle(bundle));
	};
}

export function bosatlasSearchBundles(
	title: string
): AppThunkAction<void, BosatlasBundlesAction> {
	return async (dispatch: AppThunkDispatch<BosatlasBundlesAction>) => {
		dispatch({ type: BOSATLAS_BUNDLE_FETCH_LIST });

		const bundles: BundleDto[] =
			title.length > 0
				? await searchBundles(title)
				: await getBundleList();

		dispatch(bosatlasLoadBundleList(bundles));
	};
}

export function bosatlasFetchBundleList(): AppThunkAction<
	void,
	BosatlasBundlesAction
> {
	return async (dispatch: AppThunkDispatch<BosatlasBundlesAction>) => {
		dispatch({ type: BOSATLAS_BUNDLE_FETCH_LIST });

		const bundles: BundleDto[] = await getBundleList();

		dispatch(bosatlasLoadBundleList(bundles));
	};
}

export function bosatlasUpdateBundle(
	id: string,
	title: string
): AppThunkAction<void, BosatlasBundleAction> {
	return async (dispatch: AppThunkDispatch<BosatlasBundleAction>) => {
		dispatch({ type: BOSATLAS_BUNDLE_UPDATE });

		await updateExistingBundle(id, title);

		dispatch(bosatlasFetchBundle(id));
	};
}

export function bosatlasDeleteBundle(
	id: string
): AppThunkAction<void, BosatlasBundleDeleteAction> {
	return async (dispatch: AppThunkDispatch<BosatlasBundleDeleteAction>) => {
		dispatch({ type: BOSATLAS_BUNDLE_DELETE });

		await removeBundle(id);

		dispatch({ type: BOSATLAS_BUNDLE_DELETE_COMPLETE, payload: id });
	};
}

export function bosatlasAddToBundle(
	bundle: Bundle,
	selectedProjects: SelectedProject[],
	selectedComparisons: SelectedComparison[]
): AppThunkAction<void, BosatlasBundleAction> {
	return async (dispatch: AppThunkDispatch<BosatlasBundleAction>) => {
		dispatch({ type: BOSATLAS_BUNDLE_ADD_CONTENT });
		const projectIds: string[] = selectedProjects.map(
			(project: SelectedProject) => project.id
		);
		const comparisonIds: string[] = selectedComparisons.map(
			(comparison: SelectedComparison) => comparison.id
		);

		await addToBundle(bundle.id as string, projectIds, comparisonIds);
		dispatch(bosatlasFetchBundle(bundle.id as string));
	};
}

export function bosatlasRemoveProjectFromBundle(
	bundle: Bundle,
	project: ProjectDto
): AppThunkAction<void, BosatlasBundleAction> {
	return async (dispatch: AppThunkDispatch<BosatlasBundleAction>) => {
		dispatch({ type: BOSATLAS_BUNDLE_REMOVE_PROJECT });
		const projectDetail: Project = await getProject(project.id);
		projectDetail.bundles = projectDetail.bundles.filter(
			(b: Bundle) => b.id !== bundle.id
		);
		await updateProject(projectDetail);
		dispatch(bosatlasFetchBundle(bundle.id as string));
	};
}

export function bosatlasRemoveComparisonFromBundle(
	bundle: Bundle,
	comparison: ProjectComparisonDto
): AppThunkAction<void, BosatlasBundleAction> {
	return async (dispatch: AppThunkDispatch<BosatlasBundleAction>) => {
		dispatch({ type: BOSATLAS_BUNDLE_REMOVE_COMPARISON });
		const comparisonDetail: Comparison = await getProjectComparison(
			comparison.id
		);
		comparisonDetail.bundles = comparisonDetail.bundles.filter(
			(b: Bundle) => b.id !== bundle.id
		);
		await updateProjectComparison(comparisonDetail);
		dispatch(bosatlasFetchBundle(bundle.id as string));
	};
}

export function bosatlasLoadBundle(
	bundle: BundleDetailDto
): BosatlasBundleAction {
	return {
		type: BOSATLAS_BUNDLE_LOAD,
		payload: bundleDetailDtoToBundle(bundle)
	};
}

export function bosatlasLoadBundleList(
	bundles: BundleDto[]
): BosatlasBundlesAction {
	return {
		type: BOSATLAS_BUNDLE_LOAD_LIST,
		payload: bundles.map(bundleDtoToBundle)
	};
}

export function bundleDetailDtoToBundle(dto: BundleDetailDto): Bundle {
	const comparisonAmount: number = dto.projectComparisons
		? dto.projectComparisons.length
		: 0;
	const projectAmount: number = dto.projects ? dto.projects.length : 0;
	return {
		...dto,
		id: dto.id,
		title: dto.title as string,
		projects: dto.projects,
		comparisons: dto.projectComparisons,
		contentAmount: comparisonAmount + projectAmount
	};
}

export function bundleDtoToBundle(dto: BundleDto): Bundle {
	return {
		...dto,
		id: dto.id,
		title: dto.title as string,
		contentAmount: dto.projectAmount + dto.comparisonAmount,
		projectAmount: dto.projectAmount,
		comparisonAmount: dto.comparisonAmount
	};
}
//#endregion

export function bosatlasComparisonDelete(
	comparison: ProjectComparisonDto
): AppThunkAction<void, BosatlasComparisonAction> {
	return async (dispatch: AppThunkDispatch<BosatlasComparisonAction>) => {
		await removeProjectComparison(comparison.id);
		dispatch({ type: BOSATLAS_COMPARISON_DELETE, payload: comparison });
	};
}

export function bosatlasComparisonUpdate(
	comparison: ProjectComparisonDto
): AppThunkAction<void, BosatlasComparisonAction> {
	return async (dispatch: AppThunkDispatch<BosatlasComparisonAction>) => {
		const comparisonDetail: Comparison = await getProjectComparison(
			comparison.id
		);
		comparisonDetail.title = comparison.title;
		await updateProjectComparison(comparisonDetail);

		dispatch({ type: BOSATLAS_COMPARISON_UPDATE, payload: comparison });
	};
}

export function bosatlasProjectDelete(
	project: ProjectDto
): AppThunkAction<void, BosatlasProjectAction> {
	return async (dispatch: AppThunkDispatch<BosatlasProjectAction>) => {
		await removeProject(project.id);
		dispatch({ type: BOSATLAS_PROJECT_DELETE, payload: project });
	};
}

export function bosatlasProjectUpdate(
	project: ProjectDto
): AppThunkAction<void, BosatlasProjectAction> {
	return async (dispatch: AppThunkDispatch<BosatlasProjectAction>) => {
		const projectDetail: Project = await getProject(project.id);
		projectDetail.title = project.title;
		await updateProject(projectDetail);
		dispatch({ type: BOSATLAS_PROJECT_UPDATE, payload: project });
	};
}

export function bosatlasProjectUpdateBundle(
	project: ProjectDto,
	bundles: Bundle[],
	refreshCallback?: () => void
): AppThunkAction<void, BosatlasAction> {
	return async (_: AppThunkDispatch<BosatlasAction>) => {
		const projectDetail: Project = await getProject(project.id);
		projectDetail.bundles = bundles;
		await updateProject(projectDetail);
		if (refreshCallback) {
			refreshCallback();
		}
	};
}

export function bosatlasComparisonUpdateBundle(
	comparison: ProjectComparisonDto,
	bundles: Bundle[],
	refreshCallback?: () => void
): AppThunkAction<void, BosatlasAction> {
	return async (_: AppThunkDispatch<BosatlasAction>) => {
		const comparisonDetail: Comparison = await getProjectComparison(
			comparison.id
		);
		comparisonDetail.bundles = bundles;
		await updateProjectComparison(comparisonDetail);
		if (refreshCallback) {
			refreshCallback();
		}
	};
}

export function bosatlasGetSuggestions(
	searchValue: string
): AppThunkAction<void, BosatlasSuggestionAction> {
	return async (dispatch: AppThunkDispatch<BosatlasSuggestionAction>) => {
		dispatch({ type: BOSATLAS_GET_SUGGESTIONS });
		const suggestions: string[] = await getBosatlasSearchSuggestions(
			searchValue
		);
		dispatch(bosatlasLoadSuggestions(suggestions));
	};
}

export function bosatlasLoadSuggestions(
	suggestions: string[]
): BosatlasSuggestionAction {
	return { type: BOSATLAS_LOAD_SUGGESTIONS, payload: suggestions };
}

export function bosatlasSearch(
	searchValue: string
): AppThunkAction<void, BosatlasAction> {
	return async (dispatch: AppThunkDispatch<BosatlasAction>) => {
		dispatch({ type: BOSATLAS_SEARCH });
		const bosatlas: BosatlasDto =
			searchValue !== ''
				? await searchBosatlas(searchValue)
				: await getBosatlas();

		dispatch(loadBosatlas(bosatlas));
	};
}

export function bosatlasDtoToBosatlasState(dto: BosatlasDto): IBosatlasState {
	return {
		...dto,
		bundles: dto.bundles?.map(bundleDtoToBundle)
	};
}

function trackBosatlas(bosatlas: BosatlasDto): void {
	if (bosatlas.bundles)
		sendBosatlasTrackingData('bundle', bosatlas.bundles.length);

	if (bosatlas.projects)
		sendBosatlasTrackingData('project', bosatlas.projects.length);

	if (bosatlas.projectComparisons)
		sendBosatlasTrackingData(
			'comparison',
			bosatlas.projectComparisons.length
		);
}

export function sendBosatlasTrackingData(type: string, count: number): void {
	window.tracker?.itemListDisplayed({
		type,
		order: { field: 'name', direction: 'asc' },
		count
	});
}
